import * as THREE from 'three'
import { useEffect, useRef, useState, Suspense, createContext, useContext, useMemo } from 'react'
import { Canvas, extend, useFrame, useThree } from '@react-three/fiber'
import { useCursor, MeshPortalMaterial, CameraControls, Text, Preload, useVideoTexture, Html } from '@react-three/drei'
import { useRoute, useLocation } from 'wouter'
import { easing, geometry } from 'maath'
import { suspend } from 'suspend-react'

extend(geometry)
const regular = import('@pmndrs/assets/fonts/inter_regular.woff')
const medium = import('@pmndrs/assets/fonts/inter_medium.woff')

const FrameContext = createContext(null)

function FrameProvider({ children }) {
  const [, params] = useRoute('/item/:id')
  const activeId = params?.id || null
  return <FrameContext.Provider value={{ activeId }}>{children}</FrameContext.Provider>
}

function VideoPlane({ src, frameSize, fit = 'cover', isEnlarged = false, isUnmuted = false, z = -1 }) {
  const [error, setError] = useState(false)
  const [video, setVideo] = useState(null)
  const [videoAspect, setVideoAspect] = useState(9 / 16)

  const texture = !error
    ? useVideoTexture(
        src,
        {
          unsuspend: 'loadedmetadata',
          start: true,
          muted: !isUnmuted,
          loop: true,
          crossOrigin: 'anonymous',
          playsInline: true,
          onError: () => setError(true),
        },
        (el) => {
          setVideo(el)
          const onMeta = () => {
            if (el.videoWidth && el.videoHeight) {
              setVideoAspect(el.videoWidth / el.videoHeight)
            }
          }
          if (el.readyState >= 1) onMeta()
          else el.addEventListener('loadedmetadata', onMeta, { once: true })
        }
      )
    : null

  useEffect(() => {
    if (!video) return
    video.muted = !isUnmuted
    video.play().catch(() => {})
  }, [video, isUnmuted])

  useEffect(() => {
    if (!texture) return
    texture.flipY = true
    texture.needsUpdate = true
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter
    texture.wrapS = THREE.ClampToEdgeWrapping
    texture.wrapT = THREE.ClampToEdgeWrapping
  }, [texture])

  const { width: targetW, height: targetH } = frameSize
  const portalAspect = targetW / targetH
  const overscan = isEnlarged ? 1.02 : 1.08
  const planeSize = useMemo(() => {
    let w = targetW
    let h = targetH
    if (fit === 'cover') {
      if (videoAspect > portalAspect) {
        h = targetH
        w = h * videoAspect
      } else {
        w = targetW
        h = w / videoAspect
      }
    } else {
      if (videoAspect > portalAspect) {
        w = targetW
        h = w / videoAspect
      } else {
        h = targetH
        w = h * videoAspect
      }
    }
    return [w * overscan, h * overscan, 1]
  }, [fit, targetW, targetH, videoAspect, overscan, portalAspect])

  const fallbackColor = new THREE.Color(isEnlarged ? '#303030' : '#505050')

  return (
    <group position={[0, 0, z]}>
      <mesh scale={planeSize}>
        <planeGeometry args={[1, 1]} />
        {texture ? (
          <meshBasicMaterial map={texture} toneMapped={false} side={THREE.DoubleSide} />
        ) : (
          <meshBasicMaterial color={fallbackColor} />
        )}
      </mesh>

      {error && isEnlarged && (
        <Html position={[0, 0, 0.1]} center style={{ color: 'white', fontSize: '12px' }}>
          <div style={{ padding: '10px', textAlign: 'center' }}>
            <p>Video not available</p>
            <p style={{ fontSize: '10px' }}>Check that {src} exists</p>
          </div>
        </Html>
      )}
    </group>
  )
}

export const App = () => {
  const [appReady, setAppReady] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    const timer = setTimeout(() => setAppReady(true), 500)
    return () => clearTimeout(timer)
  }, [])

  const handleError = (e) => {
    console.error('Canvas error:', e)
    setError('Could not initialize 3D scene. Please check your browser compatibility.')
  }

  return (
    <>
      {!appReady && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            background: '#f0f0f0',
            zIndex: 1000,
          }}
        >
          <div>Loading scene...</div>
        </div>
      )}

      {error && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            background: '#f0f0f0',
            zIndex: 1000,
            padding: 20,
          }}
        >
          <h2>Something went wrong</h2>
          <p>{error}</p>
        </div>
      )}

      <Canvas
        flat
        gl={{ alpha: true }}
        camera={{ fov: 75, position: [0, 0, 20] }}
        eventSource={document.getElementById('root') || undefined}
        eventPrefix="client"
        onCreated={() => console.log('Canvas created successfully')}
        onError={handleError}
      >
        {/* remove solid background color so particles show through */}
        <Suspense
          fallback={
            <Html center>
              <div style={{ color: 'black', background: 'white', padding: 10, borderRadius: 5 }}>
                Loading 3D scene...
              </div>
            </Html>
          }
        >
          <FrameProvider>
            <Frame id="01" name="Kitchen" author="" bg="#202030" position={[-1.15, 0, 0]} rotation={[0, 0.5, 0]}>
              {(isEnlarged, isUnmuted, size) => (
                <VideoPlane
                  src="/videos/video1.mp4"
                  frameSize={size}
                  fit="cover"
                  isEnlarged={isEnlarged}
                  isUnmuted={isUnmuted}
                  z={-1}
                />
              )}
            </Frame>

            <Frame id="02" name="Bar" author="" bg="#302020">
              {(isEnlarged, isUnmuted, size) => (
                <VideoPlane
                  src="/videos/video2.mp4"
                  frameSize={size}
                  fit="cover"
                  isEnlarged={isEnlarged}
                  isUnmuted={isUnmuted}
                  z={-1}
                />
              )}
            </Frame>

            <Frame id="03" name="Service" author="" bg="#203020" position={[1.15, 0, 0]} rotation={[0, -0.5, 0]}>
              {(isEnlarged, isUnmuted, size) => (
                <VideoPlane
                  src="/videos/video3.mp4"
                  frameSize={size}
                  fit="cover"
                  isEnlarged={isEnlarged}
                  isUnmuted={isUnmuted}
                  z={-1}
                />
              )}
            </Frame>
          </FrameProvider>

          <Rig />
        </Suspense>
        <Preload all />
      </Canvas>
    </>
  )
}

function Frame({ id, name, author, bg = '#151520', width = 1, height = 1.61803398875, children, ...props }) {
  const portal = useRef(null)
  const [, setLocation] = useLocation()
  const { activeId } = useContext(FrameContext) || {}
  const [hovered, hover] = useState(false)
  const isActive = activeId === id
  const [muted, setMuted] = useState(true)

  useCursor(hovered)

  useFrame((state, dt) => {
    if (portal.current) easing.damp(portal.current, 'blend', isActive ? 1 : 0, 0.2, dt)
  })

  useEffect(() => {
    if (isActive && muted) {
      const t = setTimeout(() => setMuted(false), 500)
      return () => clearTimeout(t)
    } else if (!isActive && !muted) {
      setMuted(true)
    }
  }, [isActive, muted])

  return (
    <group {...props}>
      {!isActive && (
        <>
          <Text
            font={suspend(medium).default}
            fontSize={0.22}
            anchorY="top"
            anchorX="left"
            lineHeight={0.8}
            position={[-0.375, 0.715, 0.01]}
            material-toneMapped={false}
          >
            {name}
          </Text>
          <Text
            font={suspend(regular).default}
            fontSize={0.1}
            anchorX="right"
            position={[0.4, -0.659, 0.01]}
            material-toneMapped={false}
          >
            /{id}
          </Text>
          <Text
            font={suspend(regular).default}
            fontSize={0.04}
            anchorX="right"
            position={[0.0, -0.677, 0.01]}
            material-toneMapped={false}
          >
            {author}
          </Text>
        </>
      )}

      <mesh
        name={id}
        onClick={(e) => {
          e.stopPropagation()
          setLocation(isActive ? '/' : '/item/' + e.object.name)
        }}
        onPointerOver={() => hover(true)}
        onPointerOut={() => hover(false)}
      >
        <roundedPlaneGeometry args={[width, height, 0.1]} />
        <MeshPortalMaterial ref={portal} events={isActive} side={THREE.DoubleSide}>
          <color attach="background" args={[bg]} />
          <Suspense fallback={null}>{children(isActive, !muted, { width, height })}</Suspense>

          {isActive && (
            <>
              <Html
                position={[0, height / 2 - 0.15, 0]}
                center
                style={{ pointerEvents: 'none' }}
              >
                <div
                  style={{
                    color: 'white',
                    background: 'rgba(0,0,0,0.5)',
                    padding: '5px 10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                  }}
                >
                  Click anywhere to return
                </div>
              </Html>

              <Html position={[0, -height / 2 + 0.15, 0]} center>
                <div
                  style={{
                    color: 'white',
                    background: 'rgba(0,0,0,0.5)',
                    padding: '5px 10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    setMuted((m) => !m)
                  }}
                >
                  {muted ? '🔇 Unmute' : '🔊 Mute'}
                </div>
              </Html>
            </>
          )}
        </MeshPortalMaterial>
      </mesh>
    </group>
  )
}

function Rig({ position = new THREE.Vector3(0, 0, 2), focus = new THREE.Vector3(0, 0, 0) }) {
  const { controls, scene } = useThree()
  const context = useContext(FrameContext)
  const activeId = context?.activeId || null

  useEffect(() => {
    if (!controls) return
    const active = activeId ? scene.getObjectByName(activeId) : null
    if (active) {
      try {
        active.parent.localToWorld(position.set(0, 0, 0.6))
        active.parent.localToWorld(focus.set(0, 0, -1.5))
        controls.setLookAt(...position.toArray(), ...focus.toArray(), true)
      } catch {
        position.set(0, 0, 2)
        focus.set(0, 0, 0)
        controls.setLookAt(...position.toArray(), ...focus.toArray(), true)
      }
    } else {
      position.set(0, 0, 2)
      focus.set(0, 0, 0)
      controls.setLookAt(...position.toArray(), ...focus.toArray(), true)
    }
  }, [controls, scene, activeId, position, focus])

  return (
    <CameraControls
      makeDefault
      minPolarAngle={0}
      maxPolarAngle={Math.PI / 2}
      enabled={!activeId}
      dampingFactor={0.05}
      dragToOffset
    />
  )
}
